import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { trainingService, CertificateVerification } from '../../services/trainingService';
import AcademyContentManager from './AcademyContentManager';

interface ModuleStat {
  module_id: string;
  title: string;
  category: string;
  completed_count: number;
  average_score: number | null;
  pass_rate: number;
}

interface Learner {
  user_id: number;
  email: string;
  full_name: string | null;
  completed_modules: number;
  total_modules: number;
  average_score: number | null;
  last_activity: string | null;
  certificate_code: string | null;
}

interface IssuedCertificate {
  certificate_id: string;
  verification_code: string;
  recipient_name: string;
  email: string | null;
  issue_date: string;
  average_score: number;
  curriculum_version: string;
  revoked: boolean;
}

interface TrainingOverview {
  total_learners: number;
  total_completions: number;
  certificates_issued: number;
  average_score: number | null;
  modules: ModuleStat[];
  learners: Learner[];
  certificates: IssuedCertificate[];
}

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TrainingManagement: React.FC = () => {
  const [overview, setOverview] = useState<TrainingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState<CertificateVerification | null>(null);
  const [activeView, setActiveView] = useState<'reporting' | 'content'>('reporting');
  const [revokingCode, setRevokingCode] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get<TrainingOverview>('/v1/admin/training/overview');
      setOverview(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load Academy reporting');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handleRevoke = async (certCode: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to revoke certificate ${certCode}? This action cannot be undone and will mark the credential invalid.`
    );
    if (!confirmed) return;

    setRevokingCode(certCode);
    setRevokeError(null);
    try {
      const result = await trainingService.revokeCertificate(certCode);
      setOverview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          certificates: prev.certificates.map((c) =>
            c.verification_code === certCode ? { ...c, revoked: true } : c
          ),
        };
      });
      if (verification?.certificate?.verification_code === certCode) {
        setVerification(result);
      }
    } catch (err: any) {
      setRevokeError(err.response?.data?.detail || 'Failed to revoke certificate');
    } finally {
      setRevokingCode(null);
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const result = await trainingService.verifyCertificate(code);
      setVerification(result);
    } catch {
      setVerification({
        valid: false,
        message: 'Verification service is unavailable. Try again shortly.',
        certificate: null,
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Flash Academy</h1>
          <p className="text-sm text-slate-500">
            Executive training completion, learner progress, and issued credentials.
          </p>
        </div>
        <button
          type="button"
          onClick={loadOverview}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {([
          { id: 'reporting' as const, label: 'Reporting' },
          { id: 'content' as const, label: 'Course content' },
        ]).map((view) => (
          <button
            key={view.id}
            type="button"
            onClick={() => setActiveView(view.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
              activeView === view.id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {view.label}
          </button>
        ))}
      </div>

      {activeView === 'content' ? (
        <AcademyContentManager />
      ) : (
        <>
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading && !overview ? (
        <div className="flex min-h-[240px] items-center justify-center rounded-xl border border-slate-200 bg-white">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
            <p className="text-sm text-slate-500">Loading Academy data...</p>
          </div>
        </div>
      ) : overview ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'Active learners', value: overview.total_learners.toString() },
              { label: 'Module completions', value: overview.total_completions.toString() },
              { label: 'Certificates issued', value: overview.certificates_issued.toString() },
              {
                label: 'Average score',
                value:
                  overview.average_score === null ? '—' : `${overview.average_score}%`,
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {stat.label}
                </div>
                <div className="mt-2 text-2xl font-bold text-slate-900">{stat.value}</div>
              </div>
            ))}
          </div>

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-bold text-slate-900">Module performance</h2>
              <p className="text-xs text-slate-500">
                Completion counts and pass rates across every published module.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Module</th>
                    <th className="px-5 py-3 font-semibold">Category</th>
                    <th className="px-5 py-3 font-semibold">Completed</th>
                    <th className="px-5 py-3 font-semibold">Avg score</th>
                    <th className="px-5 py-3 font-semibold">Pass rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {overview.modules.map((module) => (
                    <tr key={module.module_id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-900">{module.title}</td>
                      <td className="px-5 py-3 text-slate-500">{module.category}</td>
                      <td className="px-5 py-3 text-slate-700">{module.completed_count}</td>
                      <td className="px-5 py-3 text-slate-700">
                        {module.average_score === null ? '—' : `${module.average_score}%`}
                      </td>
                      <td className="px-5 py-3 text-slate-700">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-emerald-500"
                              style={{ width: `${Math.min(100, module.pass_rate)}%` }}
                            />
                          </div>
                          <span className="text-xs">{module.pass_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm xl:col-span-2">
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-sm font-bold text-slate-900">Learners</h2>
                <p className="text-xs text-slate-500">
                  Everyone with recorded Academy progress, newest activity first.
                </p>
              </div>
              {overview.learners.length === 0 ? (
                <p className="px-5 py-8 text-sm text-slate-500">
                  No learners have started the Academy yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Learner</th>
                        <th className="px-5 py-3 font-semibold">Modules</th>
                        <th className="px-5 py-3 font-semibold">Avg score</th>
                        <th className="px-5 py-3 font-semibold">Certificate</th>
                        <th className="px-5 py-3 font-semibold">Last activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {overview.learners.map((learner) => (
                        <tr key={learner.user_id} className="hover:bg-slate-50">
                          <td className="px-5 py-3">
                            <div className="font-medium text-slate-900">
                              {learner.full_name || learner.email}
                            </div>
                            <div className="text-xs text-slate-500">{learner.email}</div>
                          </td>
                          <td className="px-5 py-3 text-slate-700">
                            {learner.completed_modules}/{learner.total_modules}
                          </td>
                          <td className="px-5 py-3 text-slate-700">
                            {learner.average_score === null ? '—' : `${learner.average_score}%`}
                          </td>
                          <td className="px-5 py-3">
                            {learner.certificate_code ? (
                              <span className="font-mono text-xs text-emerald-700">
                                {learner.certificate_code}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Not issued</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-500">
                            {formatDate(learner.last_activity)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="space-y-6">
              <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">Verify a certificate</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Check any code a learner or insurer presents. This uses the public
                  verification endpoint.
                </p>
                <form onSubmit={handleVerify} className="mt-4 flex gap-2">
                  <input
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder="FLSH-XXXXXXXXXX"
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs uppercase text-slate-900 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={verifying || !code.trim()}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {verifying ? 'Checking...' : 'Verify'}
                  </button>
                </form>

                {revokeError && (
                  <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800">
                    {revokeError}
                  </div>
                )}

                {verification && (
                  <div
                    className={`mt-4 rounded-lg border px-3 py-3 text-xs ${
                      verification.valid
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                  >
                    <div className="font-semibold">{verification.message}</div>
                    {verification.certificate && (
                      <>
                        <dl className="mt-2 space-y-1">
                          <div className="flex justify-between gap-3">
                            <dt className="text-slate-500">Recipient</dt>
                            <dd className="text-right font-medium text-slate-800">
                              {verification.certificate.recipient_name}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-slate-500">Issued</dt>
                            <dd className="text-right text-slate-800">
                              {verification.certificate.issue_date}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-slate-500">Score</dt>
                            <dd className="text-right text-slate-800">
                              {verification.certificate.average_score}%
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-slate-500">Curriculum</dt>
                            <dd className="text-right text-slate-800">
                              v{verification.certificate.curriculum_version}
                            </dd>
                          </div>
                        </dl>
                        {verification.valid && !verification.certificate.revoked && (
                          <div className="mt-3 flex justify-end border-t border-emerald-200/70 pt-2">
                            <button
                              type="button"
                              onClick={() => verification.certificate && handleRevoke(verification.certificate.verification_code)}
                              disabled={revokingCode === verification.certificate.verification_code}
                              className="rounded-lg border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                            >
                              {revokingCode === verification.certificate.verification_code ? 'Revoking...' : 'Revoke Certificate'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-900">Issued certificates</h2>
                </div>
                {overview.certificates.length === 0 ? (
                  <p className="px-5 py-6 text-xs text-slate-500">
                     No certificates have been issued yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {overview.certificates.map((certificate) => (
                      <li key={certificate.certificate_id} className="px-5 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-slate-900">
                              {certificate.recipient_name}
                            </div>
                            <div className="truncate text-xs text-slate-500">
                              {certificate.email || '—'}
                            </div>
                            <div className="mt-1 font-mono text-[11px] text-slate-500">
                              {certificate.verification_code}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-semibold text-slate-900">
                              {certificate.average_score}%
                            </div>
                            <div className="text-[11px] text-slate-400">
                              {formatDate(certificate.issue_date)}
                            </div>
                            <div
                              className={`mt-1 text-[11px] font-semibold ${
                                certificate.revoked ? 'text-rose-600' : 'text-emerald-700'
                              }`}
                            >
                              {certificate.revoked ? 'Revoked' : 'Valid'}
                            </div>
                            {!certificate.revoked && (
                              <button
                                type="button"
                                onClick={() => handleRevoke(certificate.verification_code)}
                                disabled={revokingCode === certificate.verification_code}
                                className="mt-1 block text-right text-[11px] font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-50 underline decoration-dotted"
                              >
                                {revokingCode === certificate.verification_code ? 'Revoking...' : 'Revoke'}
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      ) : null}
        </>
      )}
    </div>
  );
};

export default TrainingManagement;
